import React, { useState, useEffect, useMemo } from "react";
import debounce from "lodash/debounce";
import { CircularProgress } from "@material-ui/core";
import { useSnackbar } from "notistack";
import CaseService from "../../../../services/CaseService";
import { getErrorMessage } from "../../../../helpers/functions";
import {
  StyledAutocomplete,
  OptionContainer,
  OptionContent,
  OptionName,
  OptionEmail,
  OptionCheck,
} from "../../NoticeGeneration/component/CustomSelect";
import { CustomInputField } from "../../../common/FormInputs";
import PartyService from "../../../../services/PartyService";

const UserSelect = ({ onSelect, classes, partyId, userId, selectedUser }) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isPrepopulated, setIsPrepopulated] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const selectedLabel =
      typeof selectedUser === "string"
        ? selectedUser
        : selectedUser?.name || "";

    if (selectedLabel) {
      setInputValue(selectedLabel);
      setIsPrepopulated(true);
    }
  }, [selectedUser]);

  const debouncedFetch = useMemo(
    () =>
      debounce(async (search) => {
        try {
          setLoading(true);

          const [cmRes, agentRes] = await Promise.all([
            CaseService.getCMlist(
              `?query=${encodeURIComponent(search)}&page=1&perPage=20`,
            ),
            PartyService.getParty(partyId),
          ]);

          const cmList =
            cmRes?.data
              ?.filter((item) => userId !== item.id)
              ?.map((item) => ({
                value: item.id,
                name: item.name,
                email: item.email,
                role: "caseManager",
                pendingDocumentCheckCount: item.pendingDocumentCheckCount,
              })) || [];

          const agentList =
            agentRes?.agents?.map((item) => ({
              value: item.userId,
              name: item.user?.name || item?.email,
              email: item.email,
              role: "agent",
              pendingDocumentCheckCount: item.pendingDocumentCheckCount,
            })) || [];

          setOptions([...cmList, ...agentList]);
        } catch (err) {
          enqueueSnackbar(getErrorMessage(err), { variant: "error" });
        } finally {
          setLoading(false);
        }
      }, 400), // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (isPrepopulated) {
      if (!inputValue || inputValue.length < 2) {
        setOptions([]);
      }
      return;
    }

    if (inputValue.length >= 2) {
      debouncedFetch(inputValue);
    } else {
      setOptions([]);
    }
  }, [inputValue, debouncedFetch, isPrepopulated]);

  return (
    <StyledAutocomplete
      classes={{
        listbox: classes?.listbox,
        noOptions: classes?.noOptions,
        paper: classes?.paper,
      }}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      options={options}
      loading={loading}
      inputValue={inputValue}
      onInputChange={(e, val) => {
        setInputValue(val);
        setIsPrepopulated(false);
      }}
      getOptionLabel={(option) => `${option.name} (${option.email})`}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      noOptionsText={
        inputValue.length < 2
          ? "Type at least 2 characters"
          : loading
            ? "Searching..."
            : "No users found"
      }
      onChange={(event, newValue) => {
        if (!newValue) return;
        onSelect(newValue);
      }}
      renderOption={(option) => (
        <OptionContainer>
          <OptionContent style={{ display: "flex", flexDirection: "column" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <OptionName>
                {option.name}{" "}
                <span
                  style={{
                    fontSize: 11,
                    fontFamily: "openSans-SemiBold, sans-serif",
                    color: "#00838c",
                  }}
                >
                  ({option.role})
                </span>
              </OptionName>
              {option.pendingDocumentCheckCount != null ? (
                <span
                  style={{
                    fontSize: 16,
                    color: "#8294a5",
                    whiteSpace: "nowrap",
                  }}
                >
                  Pending Checks: {option.pendingDocumentCheckCount}
                </span>
              ) : null}
            </div>
            <OptionEmail>{option.email}</OptionEmail>
          </OptionContent>
          <OptionCheck>✓</OptionCheck>
        </OptionContainer>
      )}
      renderInput={(params) => (
        <CustomInputField
          {...params}
          placeholder="Search checker..."
          size="small"
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={18} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default UserSelect;
